//
// Assessment main JS file
// --------------------------------------------------

// Imports
// ---------------------------------------

// import addScriptOnce from './helpers/add-script-once.js';
// import debounce from './helpers/debounce.js';
// import getPreviousSiblings from './helpers/get-previous-siblings.js';
import getSiblings from './helpers/get-siblings.js';
import renderProgressBarProgress from './progress-bar.js';
import { generateAppStateLocalstorageName } from './generateAppStateLocalstorageName.js';
// import { getMarkedUpOrdinal } from './helpers/make-number-ordinal.js';
// import { setLabelToFloatIfInputHasValue, setUpFloatingLabelInputListeners } from './floating-form-inputs.js';
import A11yDialog from 'a11y-dialog';
import setAllGraphProgressMarkers from './place-graph-markers.js';
import {
  generateTotalChart,
  generateSystemCharts,
  generateMiniSystemCharts,
  // updateAllCharts,
  // resizeAllCharts,
  destroyAllCharts,
} from './results-doughnut-charts.js';
import isoScrollspy from './isoScrollspy.js';
import { initImageSliderAutoPlay } from './image-slider.js';
import { resetAllMobileExpandedWrappers } from './mobile-expander.js';
import initReactiveToggleHiderSelects from './reactive-toggle-hider-selects.js';
import { uploadPdfWithWpAjax } from './pdf-upload.js';

// Global variables
// ---------------------------------------

// local, in-memory object for storing data
let appState = {};

// appState version and name
const appStateLsName = generateAppStateLocalstorageName();
const appStateVersion = 8;

function setAppStateToDefault() {
  appState = {
    version: appStateVersion,
    aboutUserAnswers: {},
    questions: {},
    openView: '',
    assessmentProgress: 0,
    totals: {
      'system-of-listening': {
        numQuestions: 0,
        maxScore: 0,
        totalScore: 0,
        percentageScore: 0,
      },
      'system-of-understanding-and-prediction': {
        numQuestions: 0,
        maxScore: 0,
        totalScore: 0,
        percentageScore: 0,
      },
      'system-of-action': {
        numQuestions: 0,
        maxScore: 0,
        totalScore: 0,
        percentageScore: 0,
      },
      'system-of-learning': {
        numQuestions: 0,
        maxScore: 0,
        totalScore: 0,
        percentageScore: 0,
      },
      'system-of-success': {
        numQuestions: 0,
        maxScore: 0,
        totalScore: 0,
        percentageScore: 0,
      },
    },
    grandTotal: {
      numQuestions: 0,
      maxScore: 0,
      totalScore: 0,
      percentageScore: 0,
    },
  };
}
setAppStateToDefault();

/**
 * Shows appState. Will show property if argument included. This function exists for development purposes / access in Dev Tools
 * @param {String} property - name of a property of appState
 * @returns {Object} the global appState object
 */
const getAppState = (property) => {
  if (property) {
    return appState[property];
  }
  return appState;
};
// allows dev tools' live expression to view the app state
window.getAppState = getAppState;

// storage for the results PDF blob once generated
let resultsPdfBlob = null;

// on page load, if localStorage has related data, assign it to the appState
let lsData = getLsData();

if (
  lsData &&
  Object.prototype.hasOwnProperty.call(lsData, 'assessmentProgress') &&
  lsData.assessmentProgress >= 0
) {
  appState = lsData;
}

const root = document.documentElement;
const resultsPageLoadingCover = root.querySelector(
  '.results-page-loading-cover'
);
const topLevelNavButtons = document.querySelectorAll(
  '[data-top-level-nav-button]'
);
// const firstStepInputs = document.querySelectorAll('#step-panel-0 input, #step-panel-0 select, #step-panel-0 textarea');
const gateFormInputs = document.querySelectorAll(
  '.gate-form-inputs-group input, .gate-form-inputs-group select'
);

// set up a list of the valid top-level view names
const validViewNames = ['assessment', 'results'];
// create an array of all top-level view elements
let viewEls = validViewNames.map((name) => {
  return document.querySelector(`#fe-view-${name}`);
});

const urlParams = new URLSearchParams(window.location.search);

const numberOfQuestions = Number(
  document.querySelector('.step-panel-tab-pane-wrapper').dataset
    .numberOfQuestions
);

// Initial page load
// ---------------------------------------
checkForAndHandleOutdatedStateVersions();
initUrlParams(urlParams);
// setUpFloatingLabelInputListeners(firstStepInputs);

// localStorage handling
// ---------------------------------------
function setLsData(data) {
  localStorage.setItem(appStateLsName, JSON.stringify(data));
}
function getLsData() {
  return JSON.parse(localStorage.getItem(appStateLsName));
}

// Initial page load functions
// ---------------------------------------
/**
 * check that lsData `version` matches the appState `version`
 */
function checkForAndHandleOutdatedStateVersions() {
  if (
    (lsData && !Object.prototype.hasOwnProperty.call(lsData, 'version')) ||
    (lsData && lsData.version !== appStateVersion)
  ) {
    console.warn(
      'Your localStorage’s appState version does not match the one requested. Restarting your assessment.'
    );
    lsData = null;

    restartAssessment();
  }
}

function checkForAndSetUtmParamsToFieldValues() {
  const utmInputs = document.querySelectorAll('.utm-field');
  const utmPropertyNames = Array.from(utmInputs).map(
    (utmInput) => utmInput.name
  );
  utmPropertyNames.forEach((utmPropName) => {
    if (urlParams.has(utmPropName)) {
      // console.log(`has prop name ${utmPropName}`);
      // get input of matching value
      const matchingUtmInput = document.querySelector(
        `.utm-field[name=${utmPropName}`
      );
      // console.log(
      //   'matchingUtmInput: ',
      //   matchingUtmInput,
      //   `name: ${matchingUtmInput.name}`
      // );
      // set value
      matchingUtmInput.value = urlParams.get(utmPropName);
      // console.log(
      //   `(name: ${matchingUtmInput.name}) matchingUtmInput().value: ${matchingUtmInput.value}`
      // );
    }
  });
}

function initUrlParams(urlParams) {
  checkForAndSetUtmParamsToFieldValues();

  if (!lsData) {
    console.log('No data. Going to the first question.');
    urlParams.delete('progress');
    window.history.replaceState(
      {},
      '',
      `${location.pathname}?${urlParams}${location.hash}`
    );
    goToTopLevelView('assessment');
    return false;
  }

  // if url params have the 'progress' keyword, use them for navigation
  if (urlParams.has('progress')) {
    const progress = urlParams.get('progress');

    if (progress === 'complete') {
      console.log(
        'progress is complete. confirming we are on the results view.'
      );
      if (root.dataset.viewActive !== 'results') {
        console.log('not currently on results view... opening to results');
        goToTopLevelView('results');
      }
    } else if (
      Number(progress) >= 0 &&
      Number(progress) <= numberOfQuestions + 1
    ) {
      console.log(
        `'progress' (${progress}) is between 0 and ${
          numberOfQuestions + 1
        }. Opening assessment view and going to the question indicated by progress.`
      );

      // console.log(`have the number (${progress})... now to open to that tab`);
      const tabToGoTo = document.querySelector(
        `#step-tab-item--step-panel-${progress}`
      );
      tabbedContentCloseAllSiblings(tabToGoTo);

      // Ensure the open panel starts off with the is-open class, since getActivePane, which is used up-front for current pane validation before navigating in tabbedContentOpenPanel, assumes that there is an already open pane to work with.
      const panelTheTabControls = document.getElementById(
        tabToGoTo.querySelector('.step-tab-link').getAttribute('aria-controls')
      );
      panelTheTabControls.classList.add('is-open');
      panelTheTabControls.setAttribute('aria-expanded', true);

      // console.log(
      //   'in initurlparams function. about to call tabbedContentOpenPanel'
      // );
      tabbedContentOpenPanel(tabToGoTo, false);

      goToTopLevelView('assessment');

      // if going to gate / preview results view, render scores!
      if (Number(progress) === numberOfQuestions + 1) {
        calculateScores();
        // renderScoresToEmpathyTables();
        setAllGraphProgressMarkers(appState.grandTotal.percentageScore);
      }
    } else {
      console.error(
        `The "progress" URL param does not have a valid value (currently: ${progress})`
      );
      // fall back to hash view navigation
      initUrlHashView();
    }
  }
  // if no url params or no 'progress' keyword, navigate using the hash view function
  else {
    initUrlHashView();
  }
}

function initUrlHashView() {
  // Check for a hash. If empty, go to the Assessment Page
  if (!window.location.hash) {
    console.log('No hash. Going to assessment.');
    goToTopLevelView('assessment', true);
    return false;
  }

  // Check for an invalid hash name
  else if (!validViewNames.includes(window.location.hash.slice(1))) {
    console.error('The hash is not a valid view to navigate to.');
    goToTopLevelView('assessment', true);
    return false;
  }

  // Navigate to the appropriate view given in the hash
  goToTopLevelView(window.location.hash.slice(1), true);
}

/**
 * Populate form values from saved data
 */
function populateFormFromData() {
  // // populate first step form
  // firstStepInputs.forEach((input) => {
  //   input.value = appState.aboutUserAnswers[input.name];
  //   // setLabelToFloatIfInputHasValue(input);
  // });
  // populate the steps' questions
  const questionWrappers = document.querySelectorAll('.step-panel-tab-pane');
  questionWrappers.forEach((questionWrapper) => {
    const index = Number(questionWrapper.id.split('step-panel-')[1]);
    // console.log('index: ', index);

    // skip loop iteration for index of zero or below
    if (index <= 0) {
      return;
    }

    const questionAnswer = appState.questions[index];
    // console.log('questionAnswer: ', questionAnswer);
    // if questionAnswer is falsy, skip loop iteration
    if (!questionAnswer) {
      return;
    }

    let theAnswerInput;

    // console.log(' the answers block here: ', questionWrapper);

    function getAndMarkLabelAsCheckedAndSelected(inputEl, labelClass) {
      const myLabel = inputEl.closest(labelClass);
      myLabel.classList.add('my-answer-is-checked');
      myLabel.classList.add('my-answer-is-selected');
    }

    // if the answer type uses a (single) <select>, set the select value to the answer
    if (
      questionWrapper.querySelector(
        '.answer-answers-block.has-one-select-answer'
      )
    ) {
      theAnswerInput = questionWrapper.querySelector(`select`);
      theAnswerInput.value = questionAnswer;

      getAndMarkLabelAsCheckedAndSelected(theAnswerInput, '.step-answer-label');
    }
    // if the answer type has multiple selects (eg multiple criteria ratings), handle here
    else if (
      questionWrapper.querySelector(
        '.answer-answers-block.has-multiple-select-answers'
      )
    ) {
      const criteriaSelects =
        questionWrapper.querySelectorAll('.criteria-select');
      criteriaSelects.forEach((select, index) => {
        if (
          questionAnswer[index].name === select.name &&
          questionAnswer[index].value
        ) {
          select.value = questionAnswer[index].value;
        }

        getAndMarkLabelAsCheckedAndSelected(
          select,
          '.step-answer-criteria-label'
        );
      });
    }
    // ...otherwise, grab the input by matching value, and set it to checked
    else {
      theAnswerInput = questionWrapper.querySelector(
        `input[value="${questionAnswer}"]`
      );
      try {
        theAnswerInput.checked = true;
      } catch (error) {
        console.error(
          'Catch: The theAnswerInput.checked had an error / issue. This usually comes from when localStorage has appState stored, but the number of questions in the assessment does not match.',
          error
        );
      }

      getAndMarkLabelAsCheckedAndSelected(theAnswerInput, '.step-answer-label');
    }

    // enableLocalNextQuestionButton(myLabel);
    // console.trace();
    // enableNextQuestionButton();
  });

  // populate gate form inputs
  gateFormInputs.forEach((input) => {
    const val = appState.aboutUserAnswers[input.name];
    if (val) {
      input.value = val;
    }
  });
}

// Tabbed Content Helper Functions
// ---------------------------------------
function tabbedContentCloseAllSiblings(element) {
  // console.log('running tabbedContentCloseAllSiblings');

  getSiblings(element).forEach(function (siblingEl) {
    // console.log(siblingEl);
    siblingEl.classList.remove('is-open');

    const tab = siblingEl.querySelector('.step-tab-link');
    tab.setAttribute('aria-selected', false);

    const panelTheTabControls = document.getElementById(
      tab.getAttribute('aria-controls')
    );
    panelTheTabControls.classList.remove('is-open');
    panelTheTabControls.setAttribute('aria-expanded', false);
    // console.log(panelTheTabControls);
  });
}

function tabbedContentCloseAllTabsAndTheirPanes(sharedAncestor) {
  // console.log('running tabbedContentCloseAll');

  const tabs = sharedAncestor.querySelectorAll('.step-tab-item');

  tabs.forEach((tab) => {
    tab.classList.remove('is-open');

    const tabLink = tab.querySelector('.step-tab-link');
    tabLink.setAttribute('aria-selected', false);

    const panelTheTabControls = document.getElementById(
      tabLink.getAttribute('aria-controls')
    );
    panelTheTabControls.classList.remove('is-open');
    panelTheTabControls.setAttribute('aria-expanded', false);
    // console.log(panelTheTabControls);
  });
}

/**
 * Uses the tabs structure to navigate to a tab
 * @param {Node} tab - the tab (itself, not tab link or tab panel) to go to
 * @param {Boolean} [moveFocus] - Option to turn off focus moving to the new panel, which is the default
 */
function tabbedContentOpenPanel(tab, moveFocus = true) {
  const tabIndex = Number(tab.id.split('step-tab-item--step-panel-')[1]);

  // set data attributes for feedback
  root.dataset.stepActive = tabIndex;
  // console.log('tabIndex: ', tabIndex, 'numberOfQuestions: ', numberOfQuestions);
  if (tabIndex > numberOfQuestions) {
    root.dataset.specialStep = 'preview';
  } else if (tabIndex === numberOfQuestions) {
    root.dataset.specialStep = 'last';
  } else {
    root.dataset.specialStep = 'none';
  }

  // set the new tab to go to as open
  tab.classList.add('is-open');
  const tabLink = tab.querySelector('.step-tab-link');
  tabLink.setAttribute('aria-selected', true);
  // const tabTextContent = tab.querySelector('.the-text').textContent;

  const panelTheTabControls = document.getElementById(
    tabLink.getAttribute('aria-controls')
  );

  // open the new panel affiliated with the tab
  panelTheTabControls.classList.add('is-open');
  panelTheTabControls.setAttribute('aria-expanded', true);

  // placed into a setTimeout to allow for a render essentially... here's why:
  // one of the special cases for getActivePaneAnswer is the Preview step/page. When getting answers from that, the validateGateForm function is run, which requires focusability of form inputs, which requires the inputs to be visisble. It seems on my machine that the above code, showing the panel, doesn't get it done in time for that validateGateForm to work correclty. But if I set a timeout here, even to 1ms, it works. So I guess I'll try 10 ms...
  setTimeout(() => {
    // If the panel to go to has an answer, either discovered by a change in the DOM, or by localStorage having an answer value, enable the next question button. Otherwise, disable it.
    // note that the former method is required, because the user could select an answer, then go back to the previous question, then return — in this scenario localStorage doesn't have the answer saved yet, but the change has been made in the DOM.
    // console.log(`----------\n`, panelTheTabControls);
    const panelToGoToAnswer = getActivePaneAnswer(panelTheTabControls);
    const panelToGoToAnswerFromLocalStorage =
      getActivePaneAnswerFromLocalStorage(panelTheTabControls);
    // console.log(`panelToGoToAnswer: `, panelToGoToAnswer);
    // console.log(
    //   `panelToGoToAnswerFromLocalStorage: `,
    //   panelToGoToAnswerFromLocalStorage
    // );
    if (panelToGoToAnswer || panelToGoToAnswerFromLocalStorage) {
      enableNextQuestionButton();
    } else {
      resetNextButtonsToDisabled();
    }
  }, 10);

  // If a radio herein already has the "checked" attribute, mark the label as such
  const panelAnswerOptions =
    panelTheTabControls.querySelectorAll('.step-answer-input');
  panelAnswerOptions.forEach((el) => {
    if (el.checked) {
      el.closest('.step-answer-label').classList.add('my-answer-is-checked');
    }
  });

  // Move focus to the panel itself
  window.scrollTo(0, 0);
  if (moveFocus) {
    panelTheTabControls.focus({ preventScroll: true });
  }

  return true;
}

function tabbedContentGetPrevTabSibling(el) {
  const openTabItem = el.querySelector('.step-tab-item.is-open');
  return openTabItem.previousElementSibling !== null
    ? openTabItem.previousElementSibling
    : openTabItem.parentElement.lastElementChild; // jump to end/bottom of list
}
function tabbedContentGetNextTabSibling(el) {
  const openTabItem = el.querySelector('.step-tab-item.is-open');
  return openTabItem.nextElementSibling !== null
    ? openTabItem.nextElementSibling
    : openTabItem.parentElement.firstElementChild; // jump to start/top of list
}

// // Tabbed Content Main Function
// // ---------------------------------------
// function tabbedContentMainHandler(event) {
//   // event delegation check for the event being in appropriate context. Otherwise return
//   const intendedTarget = event.target.closest(
//     '.step-panels-tab-list .step-tab-link'
//   );
//   if (!intendedTarget) {
//     return;
//   }

//   const targetElParent = event.target.closest('.step-top-area').parentNode;
//   let newElToSelect = null;

//   // ensure event is a click or a space key press
//   if (
//     event.type === 'click' ||
//     (event.type === 'keydown' && event.key === ' ')
//   ) {
//     event.preventDefault();

//     // console.log(`in tabbedContentMainHandler with an event type of ${event.type}`);

//     // prevent clicks / spacebar presses on the current element from trying to change anything
//     if (targetElParent.classList.contains('is-open')) {
//       alert('You are choosing the currently open tab; nothing has changed.');
//       return;
//     }

//     // run the open function, but if it returns false (i.e. from a current panel validation failure), do not continue this function
//     if (!tabbedContentOpenPanel(targetElParent)) {
//       // console.log(`tabbedContentOpenPanel(targetElParent) returned false.`);
//       return false;
//     }

//     tabbedContentCloseAllSiblings(targetElParent);

//     return;
//   }

//   if (event.type === 'keydown') {
//     // handle up & down arrow keys used for navigating through the tabs
//     if (
//       event.key === 'ArrowUp' ||
//       event.key === 'ArrowDown' ||
//       event.key === 'ArrowLeft' ||
//       event.key === 'ArrowRight'
//     ) {
//       event.preventDefault();

//       if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
//         newElToSelect = tabbedContentGetPrevTabSibling(
//           event.target.closest('.step-panels-tab-list')
//         );
//       } else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
//         newElToSelect = tabbedContentGetNextTabSibling(
//           event.target.closest('.step-panels-tab-list')
//         );
//       }
//     }
//   }

//   if (newElToSelect) {
//     if (tabbedContentOpenPanel(newElToSelect)) {
//       tabbedContentOpenPanel(newElToSelect);
//     } else {
//       return false;
//     }
//     newElToSelect
//       .querySelector('.step-tab-link')
//       .focus({ preventScroll: true });
//     tabbedContentCloseAllSiblings(newElToSelect);
//   }
// }

// // Tabbed Content Event Listeners
// // ---------------------------------------
// window.addEventListener('click', tabbedContentMainHandler, false);
// window.addEventListener('keydown', tabbedContentMainHandler, false);

// // Form Tab or Click Detection
// // ---------------------------------------
// let mouseIsDown = false;
// window.addEventListener('mousedown', () => (mouseIsDown = true));
// window.addEventListener('mouseup', () => {
//   setTimeout(() => {
//     mouseIsDown = false;
//   }, 20);
// });

// remove disabled from relevant ('.next-question-button')
function handleStepAnswerInputChange(event) {
  const target = event.target;
  // console.log('target is ', target, target.tagName);

  // if click is not within/on/for an answer input, return
  if (
    !(
      target.closest('.step-answer-input') ||
      target.closest('.gate-form-inputs-group')
    )
  ) {
    return false;
  }

  // if the "0" option is selected, reset next buttons to disabled, and return
  if (target.tagName === 'SELECT' && target.selectedIndex <= 0) {
    console.warn(
      'target is select and the selectedIndex is zero. about to run resetNextButtonsToDisabled'
    );
    resetNextButtonsToDisabled();
    return;
  }

  // preview page special case
  // --------------------------
  if (root.dataset.specialStep === 'preview') {
    // console.log('on preview page');
    // console.log('target: ', target, typeof target);

    const gateFormInputsGroup = target.closest('.gate-form-inputs-group');
    const gdprDisabled =
      gateFormInputsGroup.querySelector('.gdpr-field').hidden;
    const selectSelectors =
      '#ga-about-industry-wrapper select, #ga-about-country-wrapper select, #ga-about-country-wrapper:not([hidden]) select';
    let visibleFormInputs;
    if (gdprDisabled) {
      visibleFormInputs = gateFormInputsGroup.querySelectorAll(
        `input:not(#explicitConsentEmailPhone), ${selectSelectors}`
      );
    } else {
      visibleFormInputs = gateFormInputsGroup.querySelectorAll(
        `input, ${selectSelectors}`
      );
    }
    // console.log(
    //   'visibleFormInputs: ',
    //   visibleFormInputs,
    //   typeof visibleFormInputs
    // );
    const allVisibleInputsAreFilled = Array.from(visibleFormInputs).every(
      (visibleInput) => {
        // console.log(
        //   visibleInput.name,
        //   '. visibleInput.value: ',
        //   visibleInput.value,
        //   typeof visibleInput.value,
        //   visibleInput.checked
        // );
        if (visibleInput.id === 'explicitConsentEmailPhone') {
          return visibleInput.checked;
        }
        return (
          visibleInput.value &&
          visibleInput.value !== '' &&
          visibleInput.value !== 0
        );
      }
    );
    // console.log(
    //   'allVisibleInputsAreFilled: ',
    //   allVisibleInputsAreFilled,
    //   typeof allVisibleInputsAreFilled
    // );

    if (allVisibleInputsAreFilled) {
      enableNextQuestionButton();
    } else {
      resetNextButtonsToDisabled();
    }

    // console.log('on preview page. returning');
    return;
  }
  // end preview page special case
  // --------------------------

  const isCriteriaSelectInput =
    target.tagName === 'SELECT' &&
    target.closest('.step-answer-criteria-label');

  // !isCriteriaSelectInput means "standard inputs"
  if (!isCriteriaSelectInput) {
    removePaneAnswerLabelFeedbackClasses(target);
    if (target.tagName === 'SELECT' && target.selectedIndex <= 0) {
      // console.log(
      //   'in handleStepAnswerInputChange, and the target is a select, and that select has the first <option> selected, which means no answer is selected. Resetting next buttons to disabled, and returning.'
      // );
      resetNextButtonsToDisabled();
      return;
    }
    const myLabel = target.closest('.step-answer-label');
    myLabel.classList.add('my-answer-is-selected');

    // hidePanelNonSelectedAnswers(target);
    // enableLocalNextQuestionButton(target);
    enableNextQuestionButton();
  }
  // isCriteriaSelectInput
  else {
    // if the "0" option is selected, reset next buttons to disabled, and return
    if (target.tagName === 'SELECT' && target.selectedIndex <= 0) {
      resetNextButtonsToDisabled();
      return;
    }
    // notify label the answer is selected
    const myLabel = target.closest('.step-answer-criteria-label');
    myLabel.classList.add('my-answer-is-selected');

    const answersBlock = target.closest('.answer-answers-block');
    const criteriaLabels = answersBlock.querySelectorAll(
      '.step-answer-criteria-label'
    );
    const allCriteriaAreSelected = Array.from(criteriaLabels).every(
      (criteria) => criteria.classList.contains('my-answer-is-selected')
    );

    if (allCriteriaAreSelected) {
      enableNextQuestionButton();
    }
  }
}

window.addEventListener('change', handleStepAnswerInputChange);

/**
 * Removes disabled attribute from the global next question button
 */
function enableNextQuestionButton() {
  // console.trace('in enableNextQuestionButton');

  const nextButtons = document.querySelectorAll(
    '[data-next-question-button]:not(.start-assessment-button), [data-view-preview-results-button], [data-view-results-button]'
  );

  nextButtons.forEach((nextButton) => (nextButton.disabled = false));
}

/**
 * Reset next question type buttons to disabled
 */
function resetNextButtonsToDisabled() {
  // console.trace('Time to reset next buttons!');
  // (re)set all main "next" buttons to disabled
  const nextButtons = document.querySelectorAll(
    '[data-next-question-button]:not(.start-assessment-button), [data-view-results-button]'
  );
  // }
  nextButtons.forEach((nextButton) => (nextButton.disabled = true));
}

/**
 * Removes label feedback classes, e.g. when selecting another one
 * @param {Node} contextInputOrLabel
 */
function removePaneAnswerLabelFeedbackClasses(contextInputOrLabel) {
  contextInputOrLabel
    .closest('.answer-answers-block')
    .querySelectorAll('.step-answer-label')
    .forEach((myLabel) => {
      // remove these classes immediately
      myLabel.classList.remove(
        'my-answer-is-checked',
        'my-answer-is-focused',
        'my-answer-is-selected'
      );
    });

  return true;
}

/**
 * Navigates to the next question, handling side effects and data updating
 * @param {MouseEvent} event
 */
function handleNextQuestionButton(event) {
  // console.log('handling next question button');
  event.preventDefault();

  const contextPane = document.querySelector('.step-panel-tab-pane.is-open');
  const newElToSelect = tabbedContentGetNextTabSibling(
    document.querySelector('.step-panels-outer-wrapper')
  );

  const currentPaneIndex = Number(contextPane.id.split('step-panel-')[1]);
  // console.log('currentPaneIndex: ', currentPaneIndex);

  const target = event.target.closest('[data-next-question-button]');

  if (!target.classList.contains('start-assessment-button')) {
    // save active pane answer to appState
    appState.questions[currentPaneIndex] = getActivePaneAnswer(contextPane);
  }

  // save assessment progress to appState
  appState.assessmentProgress = currentPaneIndex + 1;
  // save appState to localStorage
  setLsData(appState);

  // go to next tab panel
  tabbedContentOpenPanel(newElToSelect);
  tabbedContentCloseAllSiblings(newElToSelect);

  if (target.classList.contains('preview-results-button')) {
    // console.log(
    //   'Preview results btn clicked. now running a function while i am here to populate data'
    // );
    calculateScores();
    // renderScoresToEmpathyTables();
    setAllGraphProgressMarkers(appState.grandTotal.percentageScore);
  }

  // update url parameters with progress value showing the step/question that the user is "on"
  urlParams.set('progress', appState.assessmentProgress);
  window.history.replaceState(
    {},
    '',
    `${location.pathname}?${urlParams}${location.hash}`
  );
  renderProgressBarProgress(appState.assessmentProgress);
}
const nextQuestionButtons = document.querySelectorAll(
  '[data-next-question-button]'
);
// console.log(nextQuestionButtons);
nextQuestionButtons.forEach((nextQuestionButton) => {
  nextQuestionButton.addEventListener('click', handleNextQuestionButton);
});

/**
 * Handles going back to the previous question
 * @param {MouseEvent} event
 */
function handleGoBackQuestionButton(event) {
  event.preventDefault();

  const activeTab = document.querySelector('.step-tab-item.is-open');
  // console.log('activeTab: ', activeTab);

  const newElToSelect = tabbedContentGetPrevTabSibling(
    activeTab.closest('.step-panels-outer-wrapper')
  );

  tabbedContentOpenPanel(newElToSelect);
  tabbedContentCloseAllSiblings(newElToSelect);
  // enableNextQuestionButton();
}
const goBackQuestionButtons = document.querySelectorAll(
  '[data-previous-question-button]'
);
// console.log(goBackQuestionButtons);
goBackQuestionButtons.forEach((goBackQuestionButton) => {
  goBackQuestionButton.addEventListener('click', handleGoBackQuestionButton);
});

/**
 * Validate all of the inputs in the gate mini-form, returning true if all or valid, or false if any are invalid
 * @returns {Boolean}
 */
function validateGateForm() {
  // console.log(gateFormInputs);

  let hasAnInvalidInput = false;

  const gateFormInputsGroup = document.querySelector('.gate-form-inputs-group');
  const gdprDisabled = gateFormInputsGroup.querySelector('.gdpr-field').hidden;
  const selectSelectors =
    '#ga-about-industry-wrapper select, #ga-about-country-wrapper select, #ga-about-country-wrapper:not([hidden]) select';
  let visibleFormInputs;
  if (gdprDisabled) {
    visibleFormInputs = gateFormInputsGroup.querySelectorAll(
      `input:not(#explicitConsentEmailPhone), ${selectSelectors}`
    );
  } else {
    visibleFormInputs = gateFormInputsGroup.querySelectorAll(
      `input, ${selectSelectors}`
    );
  }

  // the Array.prototype.some() method allows an iterative read through inputs, which will put the browser feedback tooltip in the correct location
  hasAnInvalidInput = Array.from(visibleFormInputs).some(
    (input) => input.reportValidity() === false
  );

  // console.log('hasAnInvalidInput', hasAnInvalidInput);
  return !hasAnInvalidInput;
}

/**
 * When View Results button is clicked, handle a variety of tasks and side effects
 * @param {MouseEvent} event - expects a click event
 */
function handleViewResultsButton(event) {
  console.log('handling view results button');
  event.preventDefault();

  // validate the gate form first. Return false if inputs are not valid
  if (!validateGateForm()) {
    return false;
  }

  // add form answers to appState
  gateFormInputs.forEach(
    (field) => (appState.aboutUserAnswers[field.name] = field.value)
  );
  setLsData(appState);

  // update progress to show 100% progress
  appState.assessmentProgress = currentPaneIndex + 1;
  setLsData(appState);

  // update progress url param to show "complete"
  urlParams.set('progress', 'complete');
  window.history.replaceState(
    {},
    '',
    `${location.pathname}?${urlParams}${location.hash}`
  );

  // move progress bar to 100% complete
  renderProgressBarProgress(appState.assessmentProgress);

  // get info
  const outermostAncestor = document.querySelector(
    '.step-panels-outer-wrapper'
  );
  // get context and index for data
  const contextPane = document.querySelector('.step-panel-tab-pane.is-open');
  const currentPaneIndex = Number(contextPane.id.split('step-panel-')[1]);

  // start the close process
  tabbedContentCloseAllTabsAndTheirPanes(outermostAncestor);

  // show results loading cover
  resultsPageLoadingCover.classList.add('is-showing');

  // open the results panel
  goToTopLevelView('results');

  // check that the pdf script is loaded. if not, retry
  if (document.querySelector('#pdf-script').dataset.isLoaded === 'true') {
    // generates pdf as blob and kicks off sending data to GAS
    assignPdfBlobThenSendData();
    console.log('out in handleViewResults: resultsPdfBlob: ', resultsPdfBlob);
  } else {
    console.warn('PDF script is not yet loaded. Retrying with delay.');
    retryPdfScriptLoadAndWait();
  }
}
const viewResultsButtons = document.querySelectorAll(
  '[data-view-results-button]'
);
// console.log(viewResultsButtons);
viewResultsButtons.forEach((viewResultsButton) => {
  viewResultsButton.addEventListener('click', handleViewResultsButton);
});

/**
 * Retry lazyload after a second before blob handling
 */
async function retryPdfScriptLoadAndWait() {
  // try loading
  loadLazyScriptTag('#pdf-script', document);

  // wait 1 second
  await new Promise((resolve) => setTimeout(resolve, 1000));
  console.log(
    'Waited 1 second. Is it loaded now?',
    document.querySelector('#pdf-script').dataset.isLoaded === 'true'
  );

  // generates pdf as blob and kicks off sending data to GAS
  assignPdfBlobThenSendData();
  // console.log('out in handleViewResults: resultsPdfBlob: ', resultsPdfBlob);
}

/**
 * Loads a script tag with src set as `data-lazy-src`
 * @param {String} scriptTagSelector - A selector string to be passed to .querySelector(), to select the specific script tag.
 * @param {Node} [contextEl=document] - A node for referring to a shared context. Defaults to document but can be made more specific.
 * @param {Function} afterLoad - A callback function. Passes the script tag as a parameter. Will be run after script laod and after marking that the script has loaded with data-is-loaded="true".
 */
function loadLazyScriptTag(
  scriptTagSelector,
  contextEl = document,
  afterLoad = () => {}
) {
  const script = contextEl.querySelector(scriptTagSelector);

  // quit if already loaded
  if (script.dataset.isLoaded === 'true') return false;

  // on load, mark script as loaded, and run the afterLoad callback funciton
  script.onload = () => {
    script.dataset.isLoaded = 'true';
    afterLoad(script);
  };

  // the loading operation: set the src from data-lazy-src
  script.setAttribute('src', script.dataset.lazySrc);
}

// /**
//  * Populates the PDF Cover page with information on who took the assessment, when, etc.
//  */
// function constructResultsPagePdfCover() {
//   const dataFillers = document.querySelectorAll('[data-linked-field]');
//   dataFillers.forEach((el) => {
//     const text = appState.aboutUserAnswers[el.dataset.linkedField];
//     el.textContent = text;
//   });

//   const dateFiller = document.querySelector('.pdf-cover-date');
//   dateFiller.textContent = getUsaStyleDate();
// }

/**
 * Populates content filled by user data
 */
function populateDataFilledContent() {
  const dataFillers = document.querySelectorAll('[data-linked-field]');
  dataFillers.forEach((el) => {
    el.textContent = appState.aboutUserAnswers[el.dataset.linkedField];
  });
}

/**
 * Populates the Results page's data relevant data points
 */
function constructResultsPageResults() {
  populateDataFilledContent();
  calculateScores();

  // console.table(appState.totals);

  // renderScoresToDevTable();
  renderPercentileScores();
  setAllGraphProgressMarkers(appState.grandTotal.percentageScore);
  renderCurrentMaturityLevel();
  renderSystemFocusLevelCopy();
  renderSystemFocusLevelContentBlocks();
  generateTotalChart(Number(appState.grandTotal.percentageScore));
  generateSystemCharts(appState.totals);
  generateMiniSystemCharts(appState.totals);
}

function calculateScores() {
  // reset appState defaults to zero so calculations are fresh
  resetCalculatedScores();

  // get all data-affiliated-system values ... from answers given
  const panelsWithAffiliatedSystem = document.querySelectorAll(
    '.step-panel-tab-pane[data-affiliated-system]'
  );
  panelsWithAffiliatedSystem.forEach((panel, index) => {
    calculateSystemScores(panel, index);
  });
  calculatePercentageScores();
  calculateGrandTotalScore();
}

function resetCalculatedScores() {
  const systems = [
    'system-of-listening',
    'system-of-understanding-and-prediction',
    'system-of-action',
    'system-of-learning',
    'system-of-success',
  ];

  systems.forEach((system) => {
    appState.totals[system].numQuestions = 0;
    appState.totals[system].maxScore = 0;
    appState.totals[system].totalScore = 0;
    appState.totals[system].percentageScore = 0;
  });
  appState.grandTotal.numQuestions = 0;
  appState.grandTotal.maxScore = 0;
  appState.grandTotal.totalScore = 0;
  appState.grandTotal.percentageScore = 0;
}

function calculateSystemScores(panel, index) {
  const system = panel.dataset.affiliatedSystem;
  // console.log('system: ', system);

  // in appState, increment the number of questions that belong to said system
  appState.totals[system].numQuestions++;

  // get this panel's highest possible points value
  const panelAnswers = panel.querySelectorAll('[data-answer-points]');
  const panelAnswerValues = [];
  panelAnswers.forEach((answerInput) => {
    panelAnswerValues.push(Number(answerInput.dataset.answerPoints));
  });
  const largestPointValue = panelAnswerValues.reduce(
    (a, b) => Math.max(a, b),
    0
  );

  // console.log('panelAnswerValues: ', panelAnswerValues);
  // console.log('max points for this panel / question: ', largestPointValue);
  appState.totals[system].maxScore += largestPointValue;

  // get this panel's chosen answer value
  const answerVal = appState.questions[index + 1];
  // console.log('answerVal is: ', answerVal);

  // get element score is attached to (have to do this because not all answer inputs are <input> tags)
  const answerEl = panel.querySelector(`[value="${answerVal}"]`);
  // console.log('answerEl is: ', answerEl);

  // get points from answer input
  const answerPoints = Number(answerEl.dataset.answerPoints);
  // console.log('answerPoints is: ', answerPoints);

  // add points to total for this sytem
  appState.totals[system].totalScore += answerPoints;
  // console.log(
  //   'appState.totals[system].totalScore after addition: ',
  //   appState.totals[system].totalScore
  // );
}

function calculatePercentageScores() {
  // loop over appState.totals
  const systems = Object.keys(appState.totals);
  // console.table(systems);
  systems.forEach((system) => {
    // get score percentage of the max
    const percentageScore = Math.round(
      (appState.totals[system].totalScore / appState.totals[system].maxScore) *
        100
    );
    // console.log(`percentageScore of ${system} is ${percentageScore}`);
    appState.totals[system].percentageScore = percentageScore
      ? percentageScore
      : 0;
  });
}

function calculateGrandTotalScore() {
  // add up totals for grand total
  for (let key in appState.totals) {
    appState.grandTotal.totalScore += appState.totals[key].totalScore;
    appState.grandTotal.maxScore += appState.totals[key].maxScore;
    appState.grandTotal.numQuestions += appState.totals[key].numQuestions;
  }
  const percentageScore = Math.round(
    (appState.grandTotal.totalScore / appState.grandTotal.maxScore) * 100
  );
  appState.grandTotal.percentageScore = percentageScore;
  // console.table(appState.grandTotal);
  //
}

// function renderScoresToDevTable() {
//   const devScoringTable = document.querySelector('#dev-scoring-results-table');
//   const devScoringTableRows = devScoringTable.querySelectorAll('tbody > tr');
//   devScoringTableRows.forEach((row) => {
//     const linkedSystem = row.dataset.linkedSystem;
//     if (linkedSystem) {
//       row.querySelector('[data-print-longform-score]').textContent =
//         appState.totals[linkedSystem].longFormScore;
//       row.querySelector('[data-print-total-score]').textContent =
//         appState.totals[linkedSystem].totalScore;
//       row.querySelector('[data-print-max-score]').textContent =
//         appState.totals[linkedSystem].maxScore;
//       row.querySelector('[data-print-score-percent]').textContent =
//         appState.totals[linkedSystem].percentageScore;
//     }

//     const rowIsGrandTotal = row.hasAttribute('data-linked-grand-total');
//     // console.log('rowIsGrandTotal: ', rowIsGrandTotal);
//     if (rowIsGrandTotal) {
//       row.querySelector('[data-print-total-score]').textContent =
//         appState.grandTotal.totalScore;
//       row.querySelector('[data-print-max-score]').textContent =
//         appState.grandTotal.maxScore;
//       row.querySelector('[data-print-score-percent]').textContent =
//         appState.grandTotal.percentageScore;
//     }
//   });
// }

// function renderScoresToEmpathyTables() {
//   const empathyTableScoreSlots = document.querySelectorAll(
//     '[data-empathy-table-your-score]'
//   );
//   empathyTableScoreSlots.forEach((scoreSlot) => {
//     scoreSlot.textContent = appState.grandTotal.percentageScore;
//   });
// }

function renderPercentileScores() {
  const percentileScoreAsPercentageSlots = document.querySelectorAll(
    '[data-render-system-percentile-score-as-percent]'
  );
  percentileScoreAsPercentageSlots.forEach((slot) => {
    const system = slot.dataset.renderSystemPercentileScoreAsPercent;
    // console.log('system: ', system);
    slot.innerHTML = `<span class="num">${appState.totals[system].percentageScore}</span><span class="num-suffix">%</span>`;
  });

  const percentileScoreSlots = document.querySelectorAll(
    '[data-render-system-percentile-score]'
  );
  percentileScoreSlots.forEach((slot) => {
    const system = slot.dataset.renderSystemPercentileScore;
    // const ordinal = getMarkedUpOrdinal(appState.totals[system].percentageScore);
    const markedUpNumber = `<span class="num">${appState.totals[system].percentageScore}</span><span class="num-suffix">%</span>`;
    // console.log('system: ', system);
    slot.innerHTML = markedUpNumber;
  });

  // grand total
  document
    .querySelectorAll('[data-render-grand-total-percentile-score]')
    .forEach((total) => {
      // const ordinal = getMarkedUpOrdinal(appState.grandTotal.percentageScore);
      const markedUpNumber = `<span class="num">${appState.grandTotal.percentageScore}</span><span class="num-suffix">%</span>`;
      // total.innerHTML = `<span class="num">${appState.grandTotal.percentageScore}</span><span class="num-suffix">%</span>`;
      total.innerHTML = markedUpNumber;
    });
}

function renderCurrentMaturityLevel() {
  // remove from all
  document
    .querySelectorAll(`.maturity-level-block`)
    .forEach((level) => level.classList.remove('you-are-here'));

  // determine current maturity level
  const score = appState.grandTotal.percentageScore;
  let maturityLevel;
  if (score < 25) {
    maturityLevel = 'transaction';
  } else if (score < 50) {
    maturityLevel = 'interaction';
  } else if (score < 75) {
    maturityLevel = 'engagement';
  } else {
    maturityLevel = 'empathy';
  }

  document
    .querySelector(
      `.maturity-level-block[data-maturity-level="${maturityLevel}"]`
    )
    .classList.add('you-are-here');
}

function sendDataToEloqua() {
  // see https://hogwarts.genesys.com/kb/eloqua-field-mapping for info on what fields Genesys has set up for Eloqua
  // console.log(`~~~~~~~~~~~~\nin sendDataToEloqua\n~~~~~~~~~~~~`);

  const url = 'https://tkelq.genesys.com/e/f2';

  // the local form element
  const form = document.querySelector('#steps-form');

  // // put full appState into the dedicated field
  // // --------------------------
  // // create copy of appState
  // let appStateCopy = getAppState();
  // // add a 'fullData' flag set to true -- might be useful later
  // appStateCopy['fullData'] = true;
  // // stringify
  // const stringyAppState = JSON.stringify(appStateCopy);
  // // get the 'full data' hidden field, and update its value to be the appStateCopy (stringified)
  // const noteCreator2Field = document.querySelector('#g-note-creator-field-2');
  // noteCreator2Field.value = stringyAppState;

  let questionText = '';
  var allQuestionTexts = [];

  function populateQuestionText() {
    // populate the steps' questions
    const questionWrappers = document.querySelectorAll('.step-panel-tab-pane');
    questionWrappers.forEach((questionWrapper) => {
      const questionTextWrapper = questionWrapper.querySelector(
        '.step-panel-main-heading'
      );
      questionText = questionTextWrapper.innerHTML;
      // console.log('Question Text is:', questionTextWrapper.innerHTML);
      allQuestionTexts.push(questionText);
    });
  }
  populateQuestionText();

  let answerText = '';
  var allAnswerTexts = [1];

  function populateAnswerText() {
    // populate the steps' questions
    const questionWrappers = document.querySelectorAll('.step-panel-tab-pane');
    // const answerWrappers = document.querySelector('.step-answer-label');
    questionWrappers.forEach((questionWrapper) => {
      const answerTextWrapper = questionWrapper.querySelector(
        '.my-answer-is-selected p'
      );
      if (answerTextWrapper !== null && answerTextWrapper.innerText) {
        answerText = answerTextWrapper.innerText;
        allAnswerTexts.push(answerText);
      }
    });
  }
  populateAnswerText();

  let allCriteriaText = [];
  var criteriaTextWrappers = document.querySelectorAll('.step-answer-criteria-label');
  let criteriaText = '';

  console.log('criteriaTextWrappers:', criteriaTextWrappers);
  criteriaTextWrappers.forEach((criteriaTextWrapper) => {
    criteriaText = criteriaTextWrapper.querySelector('.criteria-label-text').innerHTML;
    allCriteriaText.push(criteriaText);
  });

  console.log('criteriaText:', criteriaText);

  // allCriteriaText = Array.from(criteriaText);

  console.log('allCriteriaText arr', allCriteriaText);

  // function populateAnswerText() {
  //   // populate the steps' questions
  //   const questionWrappers = document.querySelectorAll('.step-panel-tab-pane');
  //   // const answerWrappers = document.querySelector('.step-answer-label');
  //   questionWrappers.forEach((questionWrapper) => {
  //     if (
  //       questionWrapper.contains(document.querySelector('.step-answer-label'))
  //     ) {
  //       const answerTextWrapper = questionWrapper
  //         .querySelector('.my-answer-is-selected')
  //         .querySelector('.label-text');
  //       answerText = answerTextWrapper.innerHTML;
  //       return answerText;
  //     } else {
  //       answerText = null;
  //     }

  //     console.log('Selected Answer Text is:', answerText);
  //     allAnswerTexts.push(answerText);
  //   });
  // }
  // populateAnswerText();

  // console.log(allQuestionTexts);
  // console.log('Answers:', allAnswerTexts);

  // put answers to questions 18, 19, & 20 into dedicated field (note...this is pretty hardcoded at this time. Ideally it would respond to some kind of setting in the admin.)
  // --------------------------
  // create copy of appState
  let appStateCopy = getAppState();
  const question18 = appStateCopy.questions[18];
  const question19 = appStateCopy.questions[19];
  // const dataForNoteCreator2 = {
  //   Q_18: appStateCopy.questions[18],
  //   Q_19: appStateCopy.questions[19],
  //   Q_20: appStateCopy.questions[20],
  // };

  const dataForNoteCreator2 = {
    reusableField01Label: allQuestionTexts[1],
    reusableField01Value: allAnswerTexts[1],
    reusableField02Label: allQuestionTexts[2],
    reusableField02Value: allAnswerTexts[2],
    reusableField03Label: allQuestionTexts[3],
    reusableField03Value: allAnswerTexts[3],
    reusableField04Label: allQuestionTexts[4],
    reusableField04Value: allAnswerTexts[4],
    reusableField05Label: allQuestionTexts[5],
    reusableField05Value: allAnswerTexts[5],
    reusableField06Label: allQuestionTexts[6],
    reusableField06Value: allAnswerTexts[6],
    reusableField07Label: allQuestionTexts[7],
    reusableField07Value: allAnswerTexts[7],
    reusableField08Label: allQuestionTexts[8],
    reusableField08Value: allAnswerTexts[8],
    reusableField09Label: allQuestionTexts[9],
    reusableField09Value: allAnswerTexts[9],
    reusableField10Label: allQuestionTexts[10],
    reusableField10Value: allAnswerTexts[10],
    reusableField11Label: allQuestionTexts[11],
    reusableField11Value: allAnswerTexts[11],
    reusableField12Label: allQuestionTexts[12],
    reusableField12Value: allAnswerTexts[12],
    reusableField13Label: allQuestionTexts[13],
    reusableField13Value: allAnswerTexts[13],
    reusableField14Label: allQuestionTexts[14],
    reusableField14Value: allAnswerTexts[14],
    reusableField15Label: allQuestionTexts[15],
    reusableField15Value: allAnswerTexts[15],
    reusableField16Label: allQuestionTexts[16],
    reusableField16Value: allAnswerTexts[16],
    reusableField17Label: allQuestionTexts[17],
    reusableField17Value: allAnswerTexts[17],
    reusableField18Label: allQuestionTexts[18],
    reusableField18Value:
      allCriteriaText[0] +
      ': ' +
      question18[0].value +
      '; ' +
      allCriteriaText[1] +
      ': ' +
      question18[1].value +
      '; ' +
      allCriteriaText[2] +
      ': ' +
      question18[2].value +
      '; ' +
      allCriteriaText[3] +
      ': ' +
      question18[3].value +
      '; ' +
      allCriteriaText[4] +
      ': ' +
      question18[4].value +
      '; ',
    reusableField19Label: allQuestionTexts[19],
    reusableField19Value:
      allCriteriaText[5] +
      ': ' +
      question19[0].value +
      '; ' +
      allCriteriaText[6] +
      ': ' +
      question19[1].value +
      '; ' +
      allCriteriaText[7] +
      ': ' +
      question19[2].value +
      '; ',
    // appStateCopy.questions[19].name[0] +
    // ',' +
    // appStateCopy.questions[19].value[0] +
    // appStateCopy.questions[19].name[1] +
    // ',' +
    // appStateCopy.questions[19].value[1] +
    // appStateCopy.questions[19].name[2] +
    // ',' +
    // appStateCopy.questions[19].value[2],
    reusableField20Label: allQuestionTexts[20],
    reusableField20Value: appStateCopy.questions[20],
  };

  console.log('dataForNoteCreator2: ', dataForNoteCreator2);
  // console.log('appStateCopy:', appStateCopy);

  // // stringify
  // const stringyDataForNoteCreator2 = JSON.stringify(dataForNoteCreator2);
  // // get the 'noteCreator2' hidden field, and update its value to be the dataForNoteCreator2 (stringified)
  // const noteCreator2Field = document.querySelector('#g-note-creator-field-2');
  // noteCreator2Field.value = stringyDataForNoteCreator2;

  // put pdf upload URL into the dedicated field
  // --------------------------
  const noteCreator1Field = document.querySelector('#g-note-creator-field');
  noteCreator1Field.value = appState.pdfUrl;

  // Create formData
  // --------------------------

  // create formData now that appState is effectively sitting in a hidden field
  const formData = new FormData(form);
  // console.log('formData:', formData);
  // append to formData
  // formData.append('foobar', fooBarValue);

  // Remove questions and answers from main formData -- these are included with appState, (see above)
  // --------------------------

  // storage for questions & answers
  // let questionsAndAnswers = [];
  let questionKeys = [];

  // store questions and answers to arrays in case they are needed later in the process
  for (let [key, value] of formData.entries()) {
    if (key.startsWith('Q_')) {
      // add to arrays
      // questionsAndAnswers.push({ [key]: value });
      questionKeys.push(key);
    }
  }
  // console.log(formData.entries());
  // console.log('questionKeys:', questionKeys);
  // remove questions & answers from formData
  questionKeys.forEach((qKey) => {
    // console.log(qKey);
    formData.delete(qKey);
  });

  for (let [key, value] of Object.entries(dataForNoteCreator2)) {
    if (key.startsWith('reusable')) {
      formData.append(key, value);
    }
  }

  // remove PDF upload endpoint related hidden fields from formData
  // --------------------------
  formData.delete('geaResultsId');
  formData.delete('geaAjaxUrl');

  // check in on formData
  // console.table([...formData.entries()]);
  // for (let [key, value] of formData) {
  //   // console.log(value);
  //   console.log('~~~ ~~~ ~~ ~ ~~ ~~~ ~~~');
  //   console.log('key: ', key);
  //   console.log('value: ', value);
  //   // console.table({ key, value });
  // }

  // Serialize formData to a URL query string
  // --------------------------
  const formDataAsQueryString = new URLSearchParams(formData).toString();
  // console.log(
  //   'formDataAsQueryString:\n',
  //   formDataAsQueryString,
  //   typeof formDataAsQueryString
  // );

  // ajax fetch to eloqua
  // --------------------------
  fetch(url, {
    method: 'POST',
    body: formDataAsQueryString,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
    },
    cache: 'no-store',
    mode: 'cors',
    credentials: 'omit',
  })
    .then((response) => console.log('Success!', response))
    .catch((error) => console.error('Error!', error.message));
}

// function sendDataToGoogleAppScriptForSpreadsheet() {
//   // the Google Apps Script versioned deploy URL
//   // initial — kylebavender@studioscience.com
//   // const scriptURL = 'https://script.google.com/macros/s/AKfycbzuAiY-uHSymbeai_G3DfaPPfEdBzTpKOr_zDiSffO8XNAU4sgiEkvBk-CVHQTIR81k/exec';
//   // testing new setup — kyle.bavender@studioscience.com
//   const scriptURL =
//     'https://script.google.com/macros/s/AKfycbyIrRm-58dFIGV8aGZOm9h3Xd1xO1u0Br06MJUET1alQOeoFswQOYpS2-3DrKzbZ5TA/exec';

//   // the local form element
//   const form = document.querySelector('#steps-form');
//   // FormData for form processing
//   // const pureFormData = new FormData(form);
//   // const formData = pureFormData;
//   const formData = new FormData(form);

//   // Send a POST to ...
//   // postTheData(suchAndSuchEndpoint, pureFormData);

//   // initialize the blob object to be sent. will override in the FileReader
//   let arrayBufferObjBlob = 'Empty...if you see this, something went wrong.';

//   // FileReader for reading the file and changing its "format" to something that can be transferred in the POST fetch request
//   const fileReader = new FileReader();

//   // this will be run as a callback after the readAsArrayBuffer() method call below, I believe
//   fileReader.onload = function () {
//     // set up an object containing filename, mimeType, and the actual bytes of the file. h/t to tanaikech/submit.md gist (https://gist.github.com/tanaikech/280b782ee0518aa083a4fe0d71384823) for having understandable syntax I can work off of. All of this is put inside of a JSON string for transfer or conversion purposes.
//     arrayBufferObjBlob = JSON.stringify({
//       filename: getPdfFilename(),
//       mimeType: 'application/pdf',
//       bytes: [...new Int8Array(fileReader.result)],
//     });

//     // add an entry for 'pdfFile', which contains the stringified blob array buffer and related data. This means it will now be submitted with the rest of the form in the POST request.
//     formData.append('pdfFile', arrayBufferObjBlob);

//     // check in on formData
//     console.log(formData);

//     // the fetch is in this function so that it will not fire until this point / the end of the FileReader process.
//     postTheData(scriptURL, formData);
//   };
//   fileReader.readAsArrayBuffer(resultsPdfBlob);

//   // the fetch that sends the actual POST request (whether to the Google Apps Script endpoint or otherwise)
//   function postTheData(url, data) {
//     fetch(url, { method: 'POST', body: data })
//       .then((response) => console.log('Success!', response))
//       .catch((error) => console.error('Error!', error.message));
//   }
// }

/**
 * Returns the first name and last name as strings in an array
 * @return {Array} Returns an array of two strings representing the first name and last name assigned in the appState
 */
function getFirstAndLastNames() {
  return [
    appState.aboutUserAnswers['firstName'],
    appState.aboutUserAnswers['lastName'],
  ];
}

/**
 * Returns the date in yyyy-mm-dd format
 * @return {string} Returns date in yyyy-mm-dd format
 */
function getDateString() {
  const now = new Date();
  return `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
}

/**
 * Returns the date in mm/dd/yyyy (USA style) format
 * @return {string} Returns date in mm/dd/yyyy format
 */
function getUsaStyleDate() {
  const now = new Date();
  return `${now.getMonth() + 1}/${now.getDate()}/${now.getFullYear()}`;
}

/**
 * Returns a constructed file name for the PDF
 * @return {string} Returns a constructed file name for the PDF
 */
function getPdfFilename() {
  // const [first, last] = getFirstAndLastNames();
  const email = appState.aboutUserAnswers['emailAddress'];
  const date = getDateString();

  const name = `Genesys-Empathy-Assessment-Results-${email}-${date}.pdf`;
  return name.toLowerCase();
}

/**
 * Detects if the device is an iOS device.
 * via https://stackoverflow.com/a/62094756/4486857
 * @returns {boolean} `true` or `false`
 */
function isiOSDevice() {
  var iosQuirkPresent = function () {
    var audio = new Audio();

    audio.volume = 0.5;
    return audio.volume === 1; // volume cannot be changed from "1" on iOS 12 and below
  };

  var isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  var isAppleDevice = navigator.userAgent.includes('Macintosh');
  var isTouchScreen = navigator.maxTouchPoints >= 1; // true for iOS 13 (and hopefully beyond)

  return isIOS || (isAppleDevice && (isTouchScreen || iosQuirkPresent()));
}

/**
 * Generates a PDF using the html2pdf library. Does quite a bit of gymnastics to get things working correctly. :)
 */
async function generatePDF() {
  const elForPdfCapture = document.getElementById('fe-view-results');

  const isiOS = isiOSDevice();
  // dance around iOS <canvas> sizing limit by using a smaller scale on iOS
  const scaleOptionForjsPDF = isiOS ? 1 : 2;
  const formatOptionForjsPDF = 'letter'; // 'letter', 'a3', [8, 10]

  const html2pdfOptions = {
    // margin: 0,
    margin: [0.2, 0],
    filename: getPdfFilename(),
    image: {
      type: 'jpeg',
      quality: 0.85,
    },
    html2canvas: {
      scale: scaleOptionForjsPDF,
      windowWidth: 1200, // simulate a browser size that causes the page's responsive CSS to output a pleasing layout in the rendered PDF
      onclone: (doc) => {
        doc.documentElement.classList.add('is-html2canvas-mode');
      },
    },
    jsPDF: {
      unit: 'in',
      format: formatOptionForjsPDF,
      orientation: 'portrait',
    },
  };

  const tempInlineColorLinks = document.querySelectorAll(
    '.pdf-temp-inline-link-color'
  );
  tempInlineColorLinks.forEach((link) => (link.style.color = '#23395D'));

  // add in case not already added
  resultsPageLoadingCover.classList.add('is-showing');

  // mitigate print issues that prevent page break prevention from working correctly -- essentially Chrome does not respect `break-inside: avoid` & similar if the parent is set to `display: flex`. "Okay, so just set the parent to `display: block` for the cloned version of this content that html2canvas creates." Except... those styles are not recalculated in time for being rendered to the PDF, or maybe even just to the <canvas>.
  // so, the solution is to add a temporary page class to change the CSS on the page, run html2pdf, and then remove that temporary class. Delays are added to give the browser time to apply everything.
  root.classList.add('is-running-html2pdf');
  if (isiOS) {
    root.classList.add('is-ios');
  }

  // wait 100ms
  await new Promise((resolve) => setTimeout(resolve, 100));

  // run html2pdf() function in worker/promise mode, and assign the global `resultsPdfBlob` variable to the pdf blob output
  // eslint-disable-next-line no-undef
  resultsPdfBlob = await html2pdf()
    // .then(() => {
    //   console.log('about to update all charts');
    //   updateAllCharts();
    //   console.log('updated all charts 1');
    // })
    // .then(() => {
    //   console.log('about to resize all charts');
    //   resizeAllCharts();
    //   console.log('resized all charts 1');
    // })
    .set(html2pdfOptions)
    .from(elForPdfCapture)
    // .then(() => {
    //   console.log('about to update all charts');
    //   updateAllCharts();
    //   console.log('updated all charts 2');
    // })
    .outputPdf('blob', 'testblobfilename.pdf');
  // console.log('after the await assignment, before the setTimeout: resultsPdfBlob: ', resultsPdfBlob);

  // after a slight delay, allowing the PDF capturing to run, remove styling overrides for sake of the PDF generation
  setTimeout(() => {
    tempInlineColorLinks.forEach((link) => link.style.removeProperty('color'));

    root.classList.remove('is-running-html2pdf');
    // hide/remove results loading cover
    resultsPageLoadingCover.classList.remove('is-showing');
  }, 100);
}

// generates pdf as blob and kicks off sending data to GAS
async function assignPdfBlobThenSendData() {
  // wait on getting the PDF generated
  await generatePDF();

  // wait on upload PDF blob and fill pdfUploadURL with response
  let pdfUploadURL = await uploadPdfWithWpAjax(
    getPdfFilename(),
    resultsPdfBlob
  );

  // add to appState so it will be included in the data sent
  appState.pdfUrl = pdfUploadURL;

  // send data (now that the `resultsPdfBlob` global variable contains the PDF blob, thanks to generatePDF() function)
  // sendDataToGoogleAppScriptForSpreadsheet();

  // send data to eloqua
  sendDataToEloqua();
}

/**
 * Downloads PDF from blob. Takes blob data via the global `resultsPdfBlob` variable, uses `URL.createObjectURL` method to create a url for the document, creates a link and assigns the href to the url, dispatches a click event to the link, and then removes the link from the page.
 * - h/t: https://dev.to/nombrekeff/download-file-from-blob-21ho
 */
function downloadPdf() {
  // trigger download text on all of them
  const downloadButtons = root.querySelectorAll('.download-pdf-button');
  downloadButtons.forEach((downloadButton) => {
    downloadButton.dataset.textShown = 'downloading';
  });

  var blobURL = URL.createObjectURL(resultsPdfBlob);
  // window.open(blobURL);

  const link = document.createElement('a');
  link.href = blobURL;
  link.download = getPdfFilename();

  // Append link to the body
  document.body.appendChild(link);

  // this is necessary as link.click() does not work on the latest firefox
  link.dispatchEvent(
    new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window,
    })
  );

  // Remove the generated link from body
  document.body.removeChild(link);

  setTimeout(() => {
    downloadButtons.forEach((downloadButton) => {
      downloadButton.dataset.textShown = 'main';
    });
  }, 2000);
}

/**
 * async helper function for making sure pdf is made before downloading
 */
async function makePDFBeforeDownloading() {
  await generatePDF();
  downloadPdf();
}

/**
 * Downloads PDF from blob if it exists. If not, attempt to generate it.
 * @param {MouseEvent} event
 */
// eslint-disable-next-line no-unused-vars
function handlePdfDownloadButton(event) {
  event.preventDefault();

  downloadAndShareDialog.hide();

  // if global variable `resultsPdfBlob` has data, download it. If not, try generating it before downloading.
  if (resultsPdfBlob) {
    downloadPdf();
  } else {
    makePDFBeforeDownloading();
  }
}
document.querySelectorAll('.download-pdf-button').forEach((dlButton) => {
  dlButton.addEventListener('click', handlePdfDownloadButton);
});

/**
 * Shares using the native browser share API if available, falling back to the clipboard API
 * @param {MouseEvent} event
 */
function handleCopyUrlButton(event) {
  const metaDescription = document.querySelector("meta[name='description']");

  const shareData = {
    title: document.title,
    url: `${location.origin}${location.pathname}`,
    text: metaDescription ? metaDescription.getAttribute('content') : '',
  };

  // // if the native browser share API is available (needs to be https; not all browsers support it either at the moment), use it
  // if (navigator.share) {
  //   console.log('okay, let us share!');

  //   navigator
  //     .share(shareData)
  //     .then(() => {
  //       console.log('Share was successful');
  //     })
  //     .catch((err) => {
  //       console.error(err);
  //     });
  // }

  // // otherwise if the Clipboard API is available, show the button and add an event listener
  // else if (navigator.clipboard) {

  if (navigator.clipboard) {
    navigator.clipboard.writeText(shareData.url).then(
      () => {
        console.log('Async: Copying to clipboard was successful!');

        const copyUrlButton = event.target.closest('[data-copy-url-button]');
        copyUrlButton.dataset.textShown = 'confirm';
        setTimeout(() => {
          copyUrlButton.dataset.textShown = 'main';
        }, 2500);
        // alert(
        //   `The URL ${shareData.url} has been copied to your clipboard for sharing.`
        // );
      },
      (err) => {
        console.error('Async: Could not copy text: ', err);
      }
    );
  } else {
    console.warn(
      `Neither the native browser share API nor the Clipboard API are available. You may need to make sure you are on an https connection on a modern browser.`
    );
    alert('Something went wrong. Select and copy the preceding URL manually.');
  }
}
document.querySelectorAll('[data-copy-url-button]').forEach((copyUrlButton) => {
  copyUrlButton.addEventListener('click', handleCopyUrlButton);
});

// function getActivePane(contextEl) {
//   let activePane = contextEl
//     .closest('.step-panels-outer-wrapper')
//     .querySelector('.step-panel-tab-pane.is-open');
//   if (!activePane) {
//     console.error('No active pane found');
//   }
//   return activePane;
// }

// function getActiveTabItem(contextEl) {
//   return contextEl
//     .closest('.step-panels-outer-wrapper')
//     .querySelector('.step-tab-item.is-open');
// }

/**
 * Returns the value of the active pane's input[type=radio]:checked. If no value found, returns null
 * @param {Node} activePane - the active pane element
 * @returns {String|Array|null|Boolean}
 */
function getActivePaneAnswer(activePane) {
  // console.trace();
  // console.log('activePane: ', activePane);

  // handle special case for preview page.
  // if the gate form is valid, this will return an array of all gate form input / select values, not just the visually active ones
  // otherwise, return is false, prompting the "next button" to be reset in tabbedContentOpenPanel
  if (activePane.id === `step-panel-${numberOfQuestions + 1}`) {
    // console.log('yep, preview pane, in getActivePaneAnswer');

    // skip all validation if the first name field is empty... think of this as a handling of when the form hasn't been filled at all yet.
    if (document.querySelector('#ga-about-first-name').value === '')
      return false;

    if (validateGateForm()) {
      // console.log('gate form is valid. Returning the answers as an array');
      const gateFormAnswers = Array.from(
        document.querySelectorAll(
          '.gate-form-inputs-group input, .gate-form-inputs-group select'
        )
      );
      return gateFormAnswers;
    } else {
      return false;
    }
  }

  // handle multiple criteria, multiple select answers cases, and return
  if (
    activePane.querySelector(
      '.answer-answers-block.has-multiple-select-answers'
    )
  ) {
    const selects = activePane.querySelectorAll('.step-answer-input');
    const allSelectsHaveAnswers = Array.from(selects).every((select) => {
      return select.selectedIndex > 0;
    });

    // if not all select inputs have answers, return false
    if (!allSelectsHaveAnswers) {
      return false;
    }

    const answerArray = Array.from(selects).map((select) => {
      const entry = {
        name: select.name,
        value: select.value,
        points:
          Number(select.options[select.selectedIndex].dataset.answerPoints) ||
          null,
      };
      // console.log('select.selectedIndex: ', select.selectedIndex);
      // console.log('entry: ', entry);
      return entry;
    });
    // console.log('answerArray: ', answerArray);

    return answerArray;
  }

  if (activePane.querySelector('.answer-answers-block.has-one-select-answer')) {
    const select = activePane.querySelector('.step-answer-input');
    // if selected <option> index is not the first (aka an answer has been chosen), get that option's value. otherwise, the answer should be null.
    const answer = select.selectedIndex > 0 ? select.value : null;

    return answer ? answer : null;
  }

  const checkedRadio = activePane.querySelector('input[type=radio]:checked');
  // console.log('checkedRadio: ', checkedRadio);
  return checkedRadio && checkedRadio.value ? checkedRadio.value : null;
}

/**
 * Returns the value of the active pane's associated answer from localStorage
 * @param {Node} activePane - the active pane element
 * @returns {String|null}
 */
function getActivePaneAnswerFromLocalStorage(activePane) {
  const activePaneNumStr = activePane.id.split('step-panel-')[1];
  // console.log('activePaneNumStr: ', activePaneNumStr);

  const lsData = getLsData();
  const answer = lsData ? lsData['questions'][activePaneNumStr] : null;
  return answer ? answer : null;
}

/**
 * Top level view control
 * @param {string} view - a string representative of the view to go to
 * @param {Boolean} [hashBasedNavigation=false] - Flag for telling the function that this call of the function was initiated by a url hash navigation. Prevents a hash from being added or replaced.
 */
function goToTopLevelView(view, hashBasedNavigation = false) {
  // validate that the view parameter is indeed a string
  if (typeof view !== 'string' || !validViewNames.includes(view)) {
    console.error(
      `The view parameter should be a string with value of ${validViewNames.map(
        (val) => `'${val}' `
      )}.`
    );
    return false;
  }

  // designate the new view element to be going to
  const newViewEl = document.querySelector(`#fe-view-${view}`);

  // turn off / hide all views
  viewEls.forEach((view) => {
    view.hidden = true;
    view.setAttribute('aria-expanded', false);
  });

  // turn on / show the new view
  newViewEl.hidden = false;
  root.dataset.viewActive = view;
  newViewEl.setAttribute('aria-expanded', true);

  // update global state object
  appState.openView = view;
  // save appState to localStorage
  setLsData(appState);

  // set the url hash
  if (!hashBasedNavigation) {
    history.replaceState(null, '', `#${view}`);
  }

  // turn off scroll restoration (https://developers.google.com/web/updates/2015/09/history-api-scroll-restoration)
  if ('scrollRestoration' in history) {
    // Back off, browser, I got this...
    history.scrollRestoration = 'manual';
  }
  // scroll to top & focus the new view
  window.scrollTo(0, 0);
  newViewEl.focus({ preventScroll: true });

  // extra tasks for certain view conditions
  // --------------------------

  // hide button if not on results
  root.querySelector('.download-and-share-button').hidden = view !== 'results';

  // what to do if certain views are targeted
  if (view === 'assessment') {
    populateFormFromData();
    initReactiveToggleHiderSelects();
    renderProgressBarProgress(appState.assessmentProgress);

    // // go to the first step/question if progress hasn't started being tracked yet
    // if (appState.assessmentProgress === 0) {
    //   appState.assessmentProgress = 1;
    //   urlParams.set('progress', '1');
    //   window.history.replaceState(
    //     {},
    //     '',
    //     `${location.pathname}?${urlParams}${location.hash}`
    //   );
    //   setLsData(appState);
    //   renderProgressBarProgress(appState.assessmentProgress);
    // }
  } else if (view === 'results') {
    // construct the results page
    // constructResultsPagePdfCover();
    constructResultsPageResults();

    // lazyload pdf script
    loadLazyScriptTag('#pdf-script', newViewEl);

    isoScrollspy('.system-section', '#system-section-nav');
  }
}

function handleTopLevelNavButton(event) {
  event.preventDefault();

  goToTopLevelView(event.target.dataset.topLevelNavButton);
}
topLevelNavButtons.forEach((topLevelNavButton) => {
  topLevelNavButton.addEventListener('click', handleTopLevelNavButton);
});

// if the skip to main content link is clicked, get the active view name from the root's data attribute, then re-apply it after some time has passed (allowing for this button to change to #main-content).
document.querySelector('.skip-link-button').addEventListener('click', () => {
  setTimeout(() => {
    location.hash = `#${root.dataset.viewActive}`;
  }, 200);
});

/**
 * use restartAssessment function
 * @param {MouseEvent} event
 */
function handleRestartAssessmentButton(event) {
  event.preventDefault();

  restartAssessment();
}

/**
 * "Start Over" / Resets app state and take user to the first / welcome panel
 */
function restartAssessment() {
  // go to assessment if not already there
  goToTopLevelView('assessment');

  // clear local state and remove local storage state
  setAppStateToDefault();
  localStorage.removeItem(appStateLsName);

  // remove progress URL param
  urlParams.delete('progress');
  window.history.replaceState(
    {},
    '',
    `${location.pathname}?${urlParams}${location.hash}`
  );

  // clear form
  const form = document.querySelector('#steps-form');
  form.reset();

  // clear classes from answers blocks indicating they are answered
  const allAnswersBlocks = document.querySelectorAll('.answer-answers-block');
  allAnswersBlocks.forEach((answersBlock) => {
    removePaneAnswerLabelFeedbackClasses(answersBlock);
  });

  // go to the first tab
  const tabToGoTo = document.querySelector(`#step-tab-item--step-panel-0`);
  tabbedContentCloseAllSiblings(tabToGoTo);
  tabbedContentOpenPanel(tabToGoTo);

  renderProgressBarProgress(0);

  // destroy charts/canvases
  destroyAllCharts();

  resetAllMobileExpandedWrappers();

  initImageSliderAutoPlay();
}

document
  .querySelector('#restart-assessment-button')
  .addEventListener('click', handleRestartAssessmentButton);

/**
 * Set values inputs wanting shortform URLs of the page (e.g. in Share dialogs)
 */
document.querySelectorAll('[data-set-value-to-shortform-url]').forEach((el) => {
  el.value = `${location.origin}${location.pathname}`;
});

//
// Initialize a11y-dialog on dialogs
// ---------------------------------------
const shareAssessmentDialogContainer = document.querySelector(
  '#share-this-assessment-dialog'
);
const shareAssessmentDialog = new A11yDialog(shareAssessmentDialogContainer);

const downloadAndShareDialogContainer = document.querySelector(
  '#download-and-share-dialog'
);
const downloadAndShareDialog = new A11yDialog(downloadAndShareDialogContainer);

//
//
// -> TODO: move to a better place
//
//
//
// Ajax file upload
// --------------------------------------------------
// let pdfUploadURL;

//
// const testUploadTempDevButton = document.querySelector(
//   '#test-upload-temp-dev-button'
// );
// testUploadTempDevButton.addEventListener('click', (event) => {
//   event.preventDefault();

//   uploadPdfWithWpAjax(getPdfFilename(), resultsPdfBlob);

//   // doesn't have it yet... the above is async due to fetch(). will need to await
//   // console.log('pdfUploadURL: ', pdfUploadURL);
// });

// //
// //
// //
// //
// //

//
function renderSystemFocusLevelCopy() {
  const itemsToFillWithSystemFocusLevels = document.querySelectorAll(
    '[data-fill-with-system-focus]'
  );

  itemsToFillWithSystemFocusLevels.forEach((item) => {
    const system = item.dataset.fillWithSystemFocus;
    const score = appState.totals[system].percentageScore;
    let focusLevel;
    if (score < 25) {
      focusLevel = 'transaction';
    } else if (score < 50) {
      focusLevel = 'interaction';
    } else if (score < 75) {
      focusLevel = 'engagement';
    } else {
      focusLevel = 'empathy';
    }

    // systemsMaturitiesCopy is assigned as a global variable in the footer; it contains the text coming from ACF, to allow translation of this dynamic sentence
    // eslint-disable-next-line no-undef
    const text = systemsMaturitiesCopy[focusLevel];
    item.innerHTML = text;
  });
}

//
function renderSystemFocusLevelContentBlocks() {
  const contentBlockWrappers = document.querySelectorAll(
    '.recommendations-block'
  );

  contentBlockWrappers.forEach((contentBlockWrapper) => {
    const system = contentBlockWrapper.dataset.system;
    const score = appState.totals[system].percentageScore;
    let focusLevel;
    if (score < 25) {
      focusLevel = 'Transaction';
    } else if (score < 50) {
      focusLevel = 'Interaction';
    } else if (score < 75) {
      focusLevel = 'Engagement';
    } else {
      focusLevel = 'Empathy';
    }

    const contentBlocks =
      contentBlockWrapper.querySelectorAll('[data-focus-level]');
    contentBlocks.forEach((contentBlock) => {
      // console.log(
      //   'contentBlock.dataset.focusLevel: ',
      //   contentBlock.dataset.focusLevel
      // );
      // console.log('focusLevel: ', focusLevel);
      contentBlock.hidden = contentBlock.dataset.focusLevel !== focusLevel;
    });
  });
}
